// app/actions/login.ts
"use server";

import { createClient } from "@/utils/supabase/server";
import { headers } from "next/headers";
import { BASE_DOMAIN } from "@/lib/config";
import { sendVerificationCodeEmail } from "@/lib/email";
import { supabaseAdmin } from "@/lib/supabaseAdmin";
import { logAuthSecurityEvent } from "@/lib/security-events";

/**
 * Type de retour standard pour toutes les actions d'authentification
 */
export type AuthActionResponse = {
  error?: string;
  success?: boolean | string;
  data?: any;
};

/**
 * Inscription d'un utilisateur
 */
export const signUpAction = async (
  formData: FormData
): Promise<AuthActionResponse> => {
  const email = formData.get("email")?.toString();
  const password = formData.get("password")?.toString();
  const supabase = await createClient();
  const origin = (await headers()).get("origin");

  if (!email || !password) {
    return { error: "Email and password are required." };
  }

  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      // après clic dans l'email, on passe dans /api/auth/callback
      emailRedirectTo: `${origin}/api/auth/callback?redirect_to=/register`,
    },
  });

  if (error) {
    console.error(error.message);
    return { error: error.message };
  }

  return {
    success:
      "Thanks for signing up! Please check your email for a verification link.",
  };
};

/**
 * Connexion par email + mot de passe
 */
export const signInAction = async (
  formData: FormData
): Promise<AuthActionResponse> => {
  const email = formData.get("email")?.toString();
  const password = formData.get("password")?.toString();
  const supabase = await createClient();

  if (!email || !password) {
    return { error: "Email and password are required." };
  }

  const { data: signInData, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    await logAuthSecurityEvent({
      event_type: "auth.login_failure",
      user_id: null,
      success: false,
      failure_reason: error.message,
      auth_method: "password",
      formData,
    });
    return { error: error.message };
  }

  if (signInData?.user) {
    await logAuthSecurityEvent({
      event_type: "auth.login_success",
      user_id: signInData.user.id,
      success: true,
      auth_method: "password",
      formData,
    });
  }
  return { success: true };
};

/**
 * Demande de réinitialisation de mot de passe
 */
// Forgot/Reset password actions live in dedicated modules under app/actions/*

/**
 * Déconnexion de l'utilisateur (reste sur la même page)
 */
export const signOutAction = async (formData?: FormData): Promise<AuthActionResponse> => {
  const supabase = await createClient();
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id ?? null;

  if (userId) {
    await logAuthSecurityEvent({
      event_type: "auth.logout",
      user_id: userId,
      success: true,
      formData: formData ?? new FormData(),
    });
  }

  await supabase.auth.signOut();
  return { success: true };
};

/* =========================================
   OTP via Supabase (magic link OTP modifié)
   ========================================= */

/**
 * Envoi d'un code OTP par email pour la connexion.
 * Utilise nos templates personnalisés au lieu des emails par défaut de Supabase.
 */
export const sendOtpAction = async (
  formData: FormData
): Promise<AuthActionResponse> => {
  const email = formData.get("email")?.toString();
  if (!email) {
    return { error: "Email is required." };
  }
  const callbackUrl = formData.get("callbackUrl")?.toString()?.trim() || "/feed";
  const supabase = await createClient();
  const origin = (await headers()).get("origin") ?? "";
  const redirectTo = callbackUrl.startsWith("/") ? callbackUrl : "/feed";

  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: false,
      emailRedirectTo: `${origin}/api/auth/callback?redirect_to=${encodeURIComponent(redirectTo)}`,
    },
  });

  if (error) {
    const msg = error.message?.toLowerCase() ?? "";
    if (msg.includes("signup") && msg.includes("not allowed") && msg.includes("otp")) {
      return { error: "This email is not registered. Please create an account first." };
    }
    return { error: error.message };
  }

  // Récupérer le profil utilisateur pour le nom
  let userName: string | undefined;
  try {
    const { data: profile } = await supabaseAdmin
      .from('profiles')
      .select('username')
      .eq('email', email)
      .single();
    userName = profile?.username;
  } catch {
    // Ignore si pas de profil
  }

  // Note: Supabase ne nous donne pas le code OTP directement
  // Pour utiliser nos templates personnalisés, il faut :
  // 1. Désactiver les emails Supabase dans le dashboard
  // 2. Utiliser l'API Admin pour générer le code manuellement
  // 3. Envoyer notre email personnalisé
  
  // Pour l'instant, Supabase enverra son email par défaut
  // Une fois les emails Supabase désactivés, on pourra utiliser notre template ici
  
  return { success: true, data };
};

/**
 * Vérification du code OTP envoyé par email pour la connexion.
 */
export const verifyOtpAction = async (
  formData: FormData
): Promise<AuthActionResponse> => {
  const email = formData.get("email")?.toString();
  const otp = formData.get("otp")?.toString();

  if (!email || !otp) {
    return { error: "Email and OTP are required." };
  }

  const supabase = await createClient();
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token: otp.trim(),
    type: "email",
  });

  if (error) {
    const code = (error as { code?: string })?.code;
    if (code === "otp_expired" || code === "token_expired") {
      const { data: userData } = await supabase.auth.getUser();
      if (userData?.user) {
        await logAuthSecurityEvent({
          event_type: "auth.login_success",
          user_id: userData.user.id,
          success: true,
          auth_method: "otp",
          formData,
        });
        return { success: true, data: { user: userData.user } };
      }
    }
    await logAuthSecurityEvent({
      event_type: "auth.login_failure",
      user_id: null,
      success: false,
      failure_reason: error.message,
      auth_method: "otp",
      formData,
    });
    return { error: error.message || "Invalid or expired OTP." };
  }

  let user = data?.user;
  if (!user) {
    const { data: userData, error: getUserError } =
      await supabase.auth.getUser();
    if (getUserError || !userData?.user) {
      return { error: "User information is missing." };
    }
    user = userData.user;
  }

  await logAuthSecurityEvent({
    event_type: "auth.login_success",
    user_id: user.id,
    success: true,
    auth_method: "otp",
    formData,
  });
  return { success: true, data: { user } };
};
